import { commonClient, type NearbySportEvent } from '@bip/api-client';
import { toSlug } from '@bip/shared';
import type { NearbyContext, NearbyFetchResult } from '../nearby.types';

/**
 * Nearby-concerts fetcher. Goes through the common-service cross-sell
 * hub (`GET /bip-api/common/nearby/concerts`), which fans out to
 * `concerts-service /nearby-concerts` server-side. Same shape as the
 * sports adapter — concerts-service reuses `getNearbySports` upstream
 * and filters by `eventType.concert: true` — so the returned
 * `NearbySportEvent[]` renders through the concert card unchanged.
 */
export async function fetchNearbyConcerts(
  ctx: NearbyContext,
): Promise<NearbyFetchResult<NearbySportEvent>> {
  const res = await commonClient.getNearbyConcerts({
    latitude: ctx.latitude,
    longitude: ctx.longitude,
    distance: 50,
    // Default fetch limit is 10 (bumped from 4) — matches every other
    // nearby adapter. The strip's Explore button surfaces whenever
    // `items.length > 4`, so the extra rows give the "there's more"
    // signal room to fire.
    limit: 10,
    sortBy: 'distance',
    sortOrder: 'asc',
    ...(ctx.dateFrom ? { dateFrom: ctx.dateFrom } : {}),
    ...(ctx.userCurrency ? { userCurrency: ctx.userCurrency } : {}),
  });

  if (!res.success || !res.data?.length) return { items: [], total: 0 };

  // Explore link lands on the concerts-domain city listing (matches the
  // sports adapter's pattern of routing to its own city page).
  const firstCity = res.data[0]?.city;
  const exploreHref = firstCity
    ? `/concerts/city/${toSlug(firstCity.name, firstCity.id)}`
    : '/concerts';

  // Preserve the backend's distance sort (spec 2026-08-06: nearby
  // concerts render nearest-first). The request explicitly asks for
  // `sortBy: 'distance'`, `sortOrder: 'asc'`; do NOT re-sort here —
  // a chronological re-sort had been overriding it, pushing farther
  // venues above closer ones whenever their event date was earlier.
  const items = res.data;

  return { items, total: res.meta.totalRecords, exploreHref };
}
